import { useState, useEffect, useRef, useMemo } from "react";
import type { Post } from "../types";
import { Heart, Bookmark, MessageCircle, MoreVertical, Eye, FileText, ChevronDown, Tag, Trash2, Archive, ArchiveRestore, Share, Star, Share2, ArrowUp, ArrowBigUp, ArrowBigDown, Globe } from "lucide-react";
import { GlobalLoader } from "./ui/GlobalLoader";
import { ThreadPopup } from "./ThreadPopup";
import { FullPostPopup } from "./FullPostPopup";
import { NoteModal } from "./NoteModal";
import { CategoryChangePopup } from "./CategoryChangePopup";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { LinkedInPollComponent } from "./LinkedInPollComponent";
import { LinkedInArticleComponent } from "./LinkedInArticleComponent";
import { processUsernames } from "../lib/utils";
import { HashtagDisplay } from "./ui/hashtag-display";
import { MediaRenderer, getMediaInfo } from "./MediaRenderer";
import { captureEvent } from "../utils/posthog";
import {
  trackPostCategorized,
  trackPostNoteAdded,
  trackPostCardOpened,
  trackPostMediaViewed,
  trackNoteEditInitiated,
  trackNoteDeleted
} from "../utils/eventTracking";
import {
  parseHtmlEntities,
  formatNumber
} from "../lib/postUtils";
import { usePostActions } from "../hooks/usePostActions";
import { getPostDisplayData, handlePostClickAction, getFavouriteState } from "../utils/postActions";
import { Category } from "@/services/api";
import { TikTokIcon } from "./TikTokIcon";
import { RedditIcon } from "./RedditIcon";
import { useImageProxy } from "../hooks/useImageProxy";
import { Checkbox } from "./ui/checkbox";

// Component for profile image with proxy support (proxy API is Instagram-only)
const ProfileImage = ({ url, alt, postId, platform, className }: { url: string | null | undefined; alt: string; postId: number; platform?: string | null; className?: string }) => {
  const proxiedUrl = useImageProxy(url, postId, 'profile_picture', platform);
  return (
    <img
      src={proxiedUrl ?? url ?? "/assets/placeholder.jpg"}
      alt={alt}
      className={className}
      onError={(e) => {
        e.currentTarget.src = "/assets/placeholder.jpg";
      }}
    />
  );
};

const FaviconImage = ({ src, alt }: { src: string; alt: string }) => {
  const [useFallback, setUseFallback] = useState(false);
  if (useFallback) {
    return (
      <div className="w-10 h-10 rounded-full bg-gray-700 flex-shrink-0 flex items-center justify-center">
        <Globe className="w-5 h-5 text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-10 h-10 rounded-full bg-gray-700 flex-shrink-0 object-cover"
      onLoad={(e) => {
        if (e.currentTarget.naturalWidth <= 16) setUseFallback(true);
      }}
      onError={() => setUseFallback(true)}
    />
  );
};

interface ReelCardProps {
  reel: Post;
  onNoteUpdate?: (postId: number, note: string) => void;
  onCategoryUpdate?: (postId: number, category?: any) => void;
  onPostDelete?: (postId: number) => void;
  onPostArchive?: (postId: number) => void;
  favouriteStates?: { [key: string]: boolean };
  onFavouriteToggle?: (postId: number, isFavourite: boolean) => void;
  categories: Category[];
  /** When true, renders a selection checkbox overlay and disables opening the post via the checkbox area. */
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (postId: number, shiftKey: boolean) => void;
}

// Helper component to render text with clickable usernames
const ProcessedText = ({ text, platform }: { text: string, platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' }) => {
  const processedParts = processUsernames(text, platform);

  return (
    <>
      {processedParts.map((part, index) => {
        if (part.type === 'username') {
          return (
            <a
              key={`username-${index}`}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-400 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              {part.content}
            </a>
          );
        } else {
          return part.content;
        }
      })}
    </>
  );
};

// Component for displaying quoted tweets
const QuotedTweet = ({ quotedTweet }: { quotedTweet: any }) => {
  // Helper function to format content with proper line breaks (for quoted tweets, don't filter empty lines)
  const formatQuotedContent = (content: string) => {
    if (!content) return '';
    const parsedContent = parseHtmlEntities(content);
    const lines = parsedContent.split('\n');
    return lines.join('\n');
  };

  const quotedMediaInfo = useMemo(() => getMediaInfo(quotedTweet), [quotedTweet]);

  // Add defensive checks for undefined author or user
  const author = quotedTweet?.author;
  const user = author?.user;
  const userName = user?.name || "Unknown User";
  const userUsername = user?.username || "unknown";
  const userProfileImage = user?.profile_image || "/assets/placeholder.jpg";

  return (
    <div className="border border-gray-600 rounded-lg p-3 my-3 bg-gray-800/50">
      <div className="flex items-start space-x-2 mb-2">
        <img
          src={userProfileImage}
          alt={userName}
          className="w-10 h-10 rounded-full"
          onError={(e) => {
            e.currentTarget.src = "/assets/placeholder.jpg";
          }}
        />
        <div>
          <div className="flex flex-col space-y-1">
            <span className="font-medium text-white text-sm">{userName}</span>
            <span className="text-gray-400 text-sm">@{userUsername}</span>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <div className="text-white text-sm leading-relaxed whitespace-pre-line">
          <ProcessedText text={formatQuotedContent(quotedTweet.content)} platform="X" />
        </div>
      </div>

      {quotedTweet.media_urls && quotedTweet.media_urls.length > 0 && (
        <div className="mb-2">
          <MediaRenderer
            mediaInfo={quotedMediaInfo}
            maxHeight="200px"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

// Component for displaying quoted LinkedIn posts
const QuotedLinkedInPost = ({ quotedPost }: { quotedPost: any }) => {
  // Helper function to format content with proper line breaks
  const formatQuotedContent = (content: string) => {
    if (!content) return '';
    const parsedContent = parseHtmlEntities(content);
    const lines = parsedContent.split('\n');
    return lines.join('\n');
  };

  const quotedMediaInfo = useMemo(() => getMediaInfo(quotedPost), [quotedPost]);

  // Add defensive checks for undefined author
  const author = quotedPost?.author;
  const userName = author?.full_name || "Unknown User";
  const userProfileImage = author?.profile_image || "/assets/placeholder.jpg";

  return (
    <div className="border border-gray-600 rounded-lg p-3 my-3 bg-gray-800/50">
      <div className="flex items-start space-x-2 mb-2">
        <img
          src={userProfileImage}
          alt={userName}
          className="w-10 h-10 rounded-full"
          onError={(e) => {
            e.currentTarget.src = "/assets/placeholder.jpg";
          }}
        />
        <div>
          <div className="flex flex-col space-y-1">
            <span className="font-medium text-white text-sm">{userName}</span>
            <span className="text-gray-400 text-sm">LinkedIn</span>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <div className="text-white text-sm leading-relaxed whitespace-pre-line">
          <ProcessedText text={formatQuotedContent(quotedPost.text)} platform="LINKEDIN" />
        </div>
      </div>

      {(quotedPost.media_urls && quotedPost.media_urls.length > 0) ? (
        <div className="mb-2">
          <MediaRenderer
            mediaInfo={quotedMediaInfo}
            maxHeight="200px"
            className="w-full"
          />
        </div>
      ) : null}
    </div>
  );
};

export const ReelCard = ({ reel, onNoteUpdate, onCategoryUpdate, onPostDelete, onPostArchive, favouriteStates, onFavouriteToggle, categories, selectionMode, isSelected, onToggleSelect }: ReelCardProps) => {
  // Defensive check for reel data
  if (!reel || typeof reel !== 'object') {
    throw new Error('Invalid reel data provided to ReelCard');
  }

  const [isThreadPopupOpen, setIsThreadPopupOpen] = useState(false);
  const [isFullPostPopupOpen, setIsFullPostPopupOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isCategoryChangePopupOpen, setIsCategoryChangePopupOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [globalLoading, setGlobalLoading] = useState({ isVisible: false, message: '' });
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  const [optimisticFavoriteState, setOptimisticFavoriteState] = useState<boolean | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle global loading state changes
  const handleGlobalLoadingChange = (isLoading: boolean, message?: string) => {
    setGlobalLoading({ isVisible: isLoading, message: message || '' });
  };

  // Use the unified post actions hook
  const {
    handleDeletePost,
    handleConfirmArchive,
    handleArchivePost,
    handleSharePost,
    handleFavouriteToggle
  } = usePostActions({
    onPostDelete,
    onPostArchive,
    onFavouriteToggle,
    favouriteStates,
    onGlobalLoadingChange: handleGlobalLoadingChange
  });

  // Get post display data using unified utility
  const postData = getPostDisplayData(reel, 256);

  // Handle clicking outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);



  const handleClick = () => {
    handlePostClickAction(reel);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };
  
  const handleViewOriginal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    handleClick();
  };

  const handleNoteUpdate = (note: string) => {
    if (onNoteUpdate) {
      onNoteUpdate(reel.id, note);
    }
    // Update local state if needed
    reel.note = note;

    // Only track note added if there's actually a note (not empty)
    if (note.trim().length > 0) {
      trackPostNoteAdded(reel.id.toString(), note.length);
    }
  };

  const handleCategoryUpdate = (postId: number, category?: any) => {
    // Call the parent callback to update the reels array
    if (onCategoryUpdate) {
      onCategoryUpdate(postId, category);
    }

    // Track category update
    if (category) {
      trackPostCategorized(postId.toString(), category.id, category.category_display_value);
    }

    captureEvent("Category updated on post", { post_id: postId, category: category?.category_enum, label: category.label });
  };

  const handleNoteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Track note edit if note already exists, otherwise it's add note initiated
    if (reel.note) {
      trackNoteEditInitiated(reel.id.toString());
    } else {
      captureEvent("add_note_initiated", { post_id: reel.id });
    }
    setIsNoteModalOpen(true);
  };

  const handleDeletePostClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    await handleDeletePost(reel.id, reel.platform);
  };

  const handleConfirmArchiveClick = async () => {
    await handleConfirmArchive(reel.id, reel.platform);
  };

  const handleArchivePostClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    await handleArchivePost(reel);
  };

  const handleSharePostClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    await handleSharePost(reel);
  };

  const handleFavouriteToggleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Prevent multiple clicks
    if (isFavoriteLoading) return;
    
    // Get current state and toggle optimistically for immediate visual feedback
    const currentState = optimisticFavoriteState !== null 
      ? optimisticFavoriteState 
      : (favouriteStates?.[reel.id.toString()] ?? reel.is_favorite ?? false);
    const newState = !currentState;
    
    // Update optimistic state immediately for instant visual feedback
    setOptimisticFavoriteState(newState);
    setIsFavoriteLoading(true);
    
    try {
      await handleFavouriteToggle(reel);
      // Clear optimistic state on success (let props take over)
      setOptimisticFavoriteState(null);
    } catch (error) {
      // Revert optimistic state on error
      setOptimisticFavoriteState(currentState);
      console.error('Failed to toggle favorite:', error);
    } finally {
      setIsFavoriteLoading(false);
    }
  };

  // Get media info
  const mediaInfo = useMemo(() => {
    try {
      let postData;
      if (reel.platform === 'X') {
        postData = reel.x_post;
      } else if (reel.platform === 'LINKEDIN') {
        postData = reel.linkedin_post;
      } else if (reel.platform === 'TIKTOK') {
        postData = reel.tiktok_post;
      } else if (reel.platform === 'REDDIT') {
        postData = reel.reddit_post;
      } else if (reel.platform === 'CHROME_BOOKMARK') {
        postData = null; // no media for bookmarks
      } else {
        postData = reel.instagram_post;
      }
      if (!postData) return null;
      const result = getMediaInfo(postData);
      return result;
    } catch (error) {
      console.warn('Failed to get media info:', error);
      return null;
    }
  }, [reel.platform, reel.x_post, reel.linkedin_post, reel.instagram_post, reel.tiktok_post, reel.reddit_post, reel.chrome_bookmark_post]);

  return (
    <div
      className={`relative bg-slate-800/50 rounded-lg p-6 border transition-colors h-full flex flex-col overflow-hidden ${isSelected ? 'border-purple-500 ring-2 ring-purple-500/60' : 'border-gray-700 hover:bg-gray-750'} ${selectionMode ? 'cursor-pointer select-none' : ''}`}
      style={{ minHeight: 0 }}
      onClick={selectionMode ? (e) => onToggleSelect?.(reel.id, e.shiftKey) : undefined}
    >
      {/* Header with User Info */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-1 min-w-0">
          {reel.platform === 'CHROME_BOOKMARK' ? (
            <div className="flex items-center space-x-3">
              <FaviconImage
                src={postData.authorInfo?.avatar ?? ''}
                alt={postData.authorInfo?.name ?? ''}
              />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-white truncate">{postData.authorInfo?.name}</div>
              </div>
            </div>
          ) : (postData.authorInfo && <div className="flex items-center space-x-3">
            <ProfileImage
              url={postData.authorInfo.avatar}
              alt={postData.authorInfo.name}
              postId={reel.id}
              platform={reel.platform}
              className="w-10 h-10 rounded-full bg-gray-600 flex-shrink-0 object-cover"
            />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-white truncate">{postData.authorInfo.name}</div>
              <div className="text-sm text-gray-400 truncate flex items-center gap-2">
                {postData.authorInfo.handle}
              </div>
            </div>
          </div>)}
        </div>
        <div className="flex items-center flex-shrink-0" ref={menuRef}>
          {selectionMode ? (
            // Visual only — the whole card handles the click (incl. shift-range) so the
            // checkbox is non-interactive and lets the event bubble to the card.
            <div className="flex items-center p-2">
              <Checkbox
                checked={!!isSelected}
                className="pointer-events-none"
                aria-label={isSelected ? `Deselect post ${reel.id}` : `Select post ${reel.id}`}
              />
            </div>
          ) : (
          <>
          {reel.category !== undefined && reel.category !== null && (reel.category.category_enum !== 'others' || reel.category.label) ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCategoryChangePopupOpen(true);
              }}
              className="hidden md:inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium max-w-[120px] truncate shrink-0 [&>svg]:size-3 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden hover:bg-purple-500/30 bg-purple-500/20 text-purple-300 border-purple-500/30 cursor-pointer"
              title="Chnage Category"
            >
              <span className="truncate">
                {reel.category.category_enum === 'others'
                  ? reel.category.label
                  : reel.category.category_display_value}
              </span>
              <ChevronDown className="w-3 h-3 flex-shrink-0" />
            </button>
          ) : (
            <div
              className="flex items-center p-2 rounded-lg border border-transparent hover:border-gray-600 cursor-pointer max-w-[120px] truncate"
              onClick={(e) => {
                e.stopPropagation();
                setIsCategoryChangePopupOpen(true);
              }}
              title='Add Category'
            >
              <Tag className="w-4 h-4 flex-shrink-0 text-gray-400" />
              <span className="truncate">{reel.category && reel.category.category_enum === 'others' ? reel.category.label : 'Add Category'}</span>
            </div>
          )}

          {/* favorite Button */}
          <button
            onClick={handleFavouriteToggleClick}
            disabled={isFavoriteLoading}
            className="flex bg-transparent items-center justify-center p-2 rounded-lg border border-transparent hover:border-gray-600 cursor-pointer ml-2 focus:outline-none focus:ring-0 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            title={(() => {
              const isFavorite = optimisticFavoriteState !== null 
                ? optimisticFavoriteState 
                : getFavouriteState(reel, favouriteStates);
              return isFavorite ? 'Unfavourite' : 'Favourite';
            })()}
          >
            <Star
              className={`w-4 h-4 ${(() => {
                const isFavorite = optimisticFavoriteState !== null 
                  ? optimisticFavoriteState 
                  : getFavouriteState(reel, favouriteStates);
                return isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 hover:text-yellow-400';
              })()}`}
            />
          </button>

          <div className="relative flex items-center" ref={menuRef}>
            <div
              className="flex items-center p-2 rounded-lg border border-transparent hover:border-gray-600 cursor-pointer"
              onClick={handleMenuClick}
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </div>

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-slate-700 border border-gray-600 rounded-lg shadow-lg z-10 min-w-[160px]">
                <div className="py-1">
                  <button
                    className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                    onClick={handleViewOriginal}
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View Original
                  </button>
                  <button
                    className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                    onClick={handleSharePostClick}
                  >
                    <Share className="w-4 h-4 mr-3" />
                    Share
                  </button>
                  <button
                    className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                    onClick={handleArchivePostClick}
                  >
                    {(reel.is_archived) ? (
                      <>
                        <ArchiveRestore className="w-4 h-4 mr-3" />
                        Unarchive
                      </>
                    ) : (
                      <>
                        <Archive className="w-4 h-4 mr-3" />
                        Archive
                      </>
                    )}
                  </button>
                  <button
                    className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors"
                    onClick={handleDeletePostClick}
                  >
                    <Trash2 className="w-4 h-4 mr-3" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>






      {/* Content */}
      <div className="mb-3">
        {reel.platform === 'REDDIT' && reel.reddit_post?.title ? (
          <div className="font-bold text-white text-sm mb-2 leading-snug">
            {reel.reddit_post.title}
          </div>
        ) : null}
        <div
          className={`text-white text-sm leading-relaxed whitespace-pre-line ${reel.platform === 'INSTAGRAM'
            ? 'line-clamp-3'
            : ''
            }`}
          title={postData.cleanedText}
        >
          <ProcessedText text={postData.truncatedText} platform={reel.platform} />
        </div>
      </div>

      {/* Chrome Bookmark — link preview card + folder breadcrumb */}
      {reel.platform === 'CHROME_BOOKMARK' && reel.chrome_bookmark_post && (
        <div className="mb-3 space-y-2">
          <a
            href={reel.chrome_bookmark_post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl overflow-hidden border border-gray-600/50 bg-gray-700/40 hover:bg-gray-700/70 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {reel.chrome_bookmark_post.og_image_url && (
              <img
                src={reel.chrome_bookmark_post.og_image_url}
                alt={reel.chrome_bookmark_post.og_title || reel.chrome_bookmark_post.title || ''}
                className="w-full h-36 object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div className="px-3 py-2">
              {(reel.chrome_bookmark_post.og_title || reel.chrome_bookmark_post.title) && (
                <p className="text-sm font-medium text-white line-clamp-2 leading-snug">
                  {reel.chrome_bookmark_post.og_title || reel.chrome_bookmark_post.title}
                </p>
              )}
              {reel.chrome_bookmark_post.og_description && (
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-snug">
                  {reel.chrome_bookmark_post.og_description}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1 truncate">{reel.chrome_bookmark_post.url}</p>
            </div>
          </a>
          {reel.chrome_bookmark_post.folder_path && reel.chrome_bookmark_post.folder_path.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
              <span className="truncate">{reel.chrome_bookmark_post.folder_path.join(' › ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Hashtags */}
      {postData.hashtags.length > 0 && (
        <div className="mb-3">
          <HashtagDisplay hashtags={postData.hashtags} />
        </div>
      )}

      {/* Media - only show if post has media */}
      {postData.hasMedia && (
        <div className="mb-4 overflow-hidden" style={{ maxHeight: "300px" }}>
          <MediaRenderer
            mediaInfo={mediaInfo}
            maxHeight="300px"
            className="w-full"
            post={reel}
            onMediaView={(mediaType) => trackPostMediaViewed(reel.id.toString(), mediaType)}
          />
        </div>
      )}

      {reel.platform === 'X' && reel.x_post?.quoted_tweet && (
        <QuotedTweet quotedTweet={reel.x_post.quoted_tweet} />
      )}

      {/* Quoted Post - for LinkedIn posts */}
      {reel.platform === 'LINKEDIN' && reel.linkedin_post?.quoted_post && (
        <QuotedLinkedInPost quotedPost={reel.linkedin_post.quoted_post} />
      )}

      {/* LinkedIn Poll Component */}
      {reel.platform === 'LINKEDIN' && reel.linkedin_post?.poll_component && (
        <LinkedInPollComponent pollComponent={reel.linkedin_post.poll_component} />
      )}

      {/* LinkedIn Article Component */}
      {reel.platform === 'LINKEDIN' && reel.linkedin_post?.article_component && (
        <LinkedInArticleComponent articleComponent={reel.linkedin_post.article_component} />
      )}

      {/* Note Display - only show when note exists */}
      {reel.note && (
        <div className="mb-3 p-4 bg-gray-700/50 border border-orange-500/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-orange-400" />
              <span className="text-orange-400 text-sm font-medium">Your Note</span>
            </div>
            <div
              onClick={handleNoteClick}
              className="text-orange-400 hover:text-orange-300 p-2 hover:bg-orange-300/20 rounded-lg transition-colors bg-transparent"
              title="Edit Note"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pen-line h-3 w-3"><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>
            </div>
          </div>
          <div className="text-white text-sm leading-relaxed">
            {reel.note}
          </div>
        </div>
      )}

      {/* Static Button List - Read Thread, Read Post, Add Note */}
      <div className="mb-4 flex items-center space-x-2">
        {/* Read Thread Button - only show for threads */}
        {postData.isThread && (
          <div
            className="flex items-center space-x-2 bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660] px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer text-xs whitespace-nowrap min-w-0"
            onClick={(e) => {
              e.stopPropagation();
              trackPostCardOpened(reel.id.toString(), reel.category?.category_display_value || 'uncategorized', reel.platform);
              setIsThreadPopupOpen(true);
            }}
          >
            <span>Read Thread • {reel.x_post?.tweet_threads?.length ? reel.x_post.tweet_threads.length + 1 : 1} posts</span>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}

        {/* Read Post Button - only show for truncated X posts that are not threads */}
        {!postData.isThread && postData.isTruncated && (
          <button
            className="flex items-center space-x-2 bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660] px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer text-xs whitespace-nowrap min-w-0"
            onClick={(e) => {
              e.stopPropagation();
              trackPostCardOpened(reel.id.toString(), reel.category?.category_display_value || 'uncategorized', reel.platform);
              setIsFullPostPopupOpen(true);
            }}
          >
            <span>Read full post</span>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* Add Note Button - only show when no note exists */}
        {!reel.note && (
          <button
            onClick={handleNoteClick}
            className="flex items-center space-x-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-xs transition-colors bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660] whitespace-nowrap min-w-0"
          >
            <FileText className="w-4 h-4" />
            <span>Add Note</span>
          </button>
        )}
      </div>

      {/* Engagement Metrics - only show for X posts */}
      {reel.platform === 'X' && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Heart className="w-4 h-4" />
              <span>{formatNumber(postData.metrics.likes)}</span>
            </div>
            {'saves' in postData.metrics && (
              <div className="flex items-center space-x-1">
                <Bookmark className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.saves)}</span>
              </div>
            )}
            {'comments' in postData.metrics && (
              <div className="flex items-center space-x-1">
                <MessageCircle className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.comments)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-blue-400">
              <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
            </svg>
            <span>{postData.postTime}</span>
          </div>
        </div>
      )}

      {/* Time only for Instagram posts (no engagement metrics) */}
      {reel.platform === 'INSTAGRAM' && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            {postData.metrics.likes !== undefined && postData.metrics.likes > 0 && (
              <div className="flex items-center space-x-1">
                <Heart className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.likes)}</span>
              </div>
            )}
            {'play' in postData.metrics && postData.metrics.play !== undefined && postData.metrics.play !== null && postData.metrics.play > 0 && (
              <div className="flex items-center space-x-1">
                <Eye className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.play)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-pink-400">
              <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
            </svg>
            <span>{postData.postTime ?? ''}</span>
          </div>
        </div>
      )}

      {/* Engagement metrics for LinkedIn posts */}
      {reel.platform === 'LINKEDIN' && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            {postData.metrics.likes !== undefined && (
              <div className="flex items-center space-x-1">
                <Heart className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.likes)}</span>
              </div>
            )}
            {'shares' in postData.metrics && postData.metrics.shares !== undefined && (
              <div className="flex items-center space-x-1">
                <Share2 className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.shares)}</span>
              </div>
            )}
            {'comments' in postData.metrics && postData.metrics.comments !== undefined && (
              <div className="flex items-center space-x-1">
                <MessageCircle className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.comments)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-blue-400">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
              <rect width="4" height="12" x="2" y="9"></rect>
              <circle cx="4" cy="4" r="2"></circle>
            </svg>
            <span>{postData.postTime ?? ''}</span>
          </div>
        </div>
      )}

      {/* Engagement metrics for TikTok posts */}
      {reel.platform === 'TIKTOK' && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            {postData.metrics.likes !== undefined && postData.metrics.likes > 0 && (
              <div className="flex items-center space-x-1">
                <Heart className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.likes)}</span>
              </div>
            )}
            {'play' in postData.metrics && postData.metrics.play !== undefined && postData.metrics.play > 0 && (
              <div className="flex items-center space-x-1">
                <Eye className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.play)}</span>
              </div>
            )}
            {'comments' in postData.metrics && postData.metrics.comments !== undefined && postData.metrics.comments > 0 && (
              <div className="flex items-center space-x-1">
                <MessageCircle className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.comments)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <TikTokIcon className="w-4 h-4" />
            <span>{postData.postTime ?? ''}</span>
          </div>
        </div>
      )}

      {/* Engagement metrics for Reddit posts */}
      {reel.platform === 'REDDIT' && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            {postData.metrics.likes !== undefined && postData.metrics.likes > 0 && (
              <div className="flex items-center space-x-1">
                <ArrowBigUp className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.likes)}</span>
              </div>
            )}
            {'comments' in postData.metrics && postData.metrics.comments !== undefined && postData.metrics.comments > 0 && (
              <div className="flex items-center space-x-1">
                <MessageCircle className="w-4 h-4" />
                <span>{formatNumber(postData.metrics.comments)}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <RedditIcon className="w-4 h-4" />
            <span>{postData.postTime ?? ''}</span>
          </div>
        </div>
      )}

      {/* Chrome Bookmark — saved date footer */}
      {reel.platform === 'CHROME_BOOKMARK' && (
        <div className="flex items-center justify-end text-xs text-gray-500 mt-auto pt-1">
          <span>{postData.postTime} ago</span>
        </div>
      )}

      {/* Thread Popup */}
      {isThreadPopupOpen && reel.platform === 'X' && reel.x_post?.tweet_threads && (
        <ThreadPopup
          author={reel.x_post.author}
          originalTweet={reel.x_post}
          threads={reel.x_post.tweet_threads}
          onClose={() => setIsThreadPopupOpen(false)}
        />
      )}

      {/* Full Post Popup */}
      {isFullPostPopupOpen && (() => {
        let postDataForPopup;
        if (reel.platform === 'X') {
          postDataForPopup = reel.x_post;
        } else if (reel.platform === 'LINKEDIN') {
          postDataForPopup = reel.linkedin_post;
        } else if (reel.platform === 'TIKTOK') {
          postDataForPopup = reel.tiktok_post;
        } else if (reel.platform === 'REDDIT') {
          postDataForPopup = reel.reddit_post;
        } else if (reel.platform === 'CHROME_BOOKMARK') {
          postDataForPopup = reel.chrome_bookmark_post;
        } else {
          postDataForPopup = reel.instagram_post;
        }

        return (
          <FullPostPopup
            post={postDataForPopup}
            author={postData.authorInfo}
            platform={reel.platform}
            postId={reel.id}
            onClose={() => setIsFullPostPopupOpen(false)}
          />
        );
      })()}

      {/* Note Modal */}
      {isNoteModalOpen && (
        <NoteModal
          post={reel}
          isOpen={isNoteModalOpen}
          onClose={() => setIsNoteModalOpen(false)}
          onNoteUpdate={handleNoteUpdate}
          onNoteDelete={(postId, wordCount) => trackNoteDeleted(postId, wordCount)}
        />
      )}

      {/* Category Change Popup */}
      {isCategoryChangePopupOpen && (
        <CategoryChangePopup
          post={reel}
          isOpen={isCategoryChangePopupOpen}
          onClose={() => setIsCategoryChangePopupOpen(false)}
          onCategoryUpdate={handleCategoryUpdate}
          categories={categories}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        onArchive={handleConfirmArchiveClick}
        isArchived={reel.is_archived}
      />

      {/* Global Loader for Archive/Delete Operations */}
      <GlobalLoader 
        isVisible={globalLoading.isVisible} 
        message={globalLoading.message} 
      />
    </div>
  );
};